/**
 * useNotifications Hook
 * Custom hook to fetch and manage notification state
 * [v0.10.0] Extracted from NotificationCenter for reuse across components
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getNotifications,
  type Notification,
} from '../services/notificationsApi';

interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage notifications
 * Automatically polls for new notifications every 3 minutes when authenticated
 */
export const useNotifications = (poll: boolean = true): UseNotificationsResult => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    // Double-check authentication before making API call
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await getNotifications({ limit: 10 });
      if (result.success && result.data) {
        setNotifications(result.data.data);
        setUnreadCount(result.data.pagination.unreadCount);
      } else if (result.error) {
        // Handle API errors gracefully (404, 500, etc.)
        setNotifications([]);
        setUnreadCount(0);
        setError(result.error?.message || 'Failed to fetch notifications');
      }
    } catch (err) {
      // Silently handle network errors or 404s
      setNotifications([]);
      setUnreadCount(0);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Initial fetch when authenticated
    if (isAuthenticated) {
      fetchNotifications();
    }
  }, [isAuthenticated, fetchNotifications]);

  // Poll for new notifications every 3 minutes (only if authenticated and polling enabled)
  useEffect(() => {
    if (!poll || !isAuthenticated) {
      return;
    }

    const pollInterval = setInterval(() => {
      fetchNotifications();
    }, 180000); // 3 minutes

    return () => clearInterval(pollInterval);
  }, [fetchNotifications, isAuthenticated, poll]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    refetch: fetchNotifications,
  };
};
